import { Box } from '@material-ui/core';
import React, { useEffect, useMemo } from 'react';
import { dummyData, useYourFiles } from 'src/lib/contexts/YourFilesContext';
import IconAddDocs from 'src/components/Icons/IconAddDocs';
import IconAddPhoto from 'src/components/Icons/IconAddPhoto';
import Row from 'src/components/Row';
import DocumentActionCard from '../components/DocumentActionCard';
import LoadingDocumentCard from '../components/LoadingDocumentCard';
import { DocumentsProps } from '../YourFileTypes';
import FIleEmptyState from '../components/FIleEmptyState';

const FILE_EXTENSIONS = {
    DOCUMENTS: new Set([
        'doc',
        'docx',
        'docm',
        'html',
        'xls',
        'xlsx',
        'xlsm',
        'ppt',
        'pptx',
        'pptm',
        'pub',
        'txt',
        'pdf',
    ]),
    IMAGES: new Set([
        'jpg',
        'jpeg',
        'jpe',
        'jfif',
        'gif',
        'bmp',
        'tiff',
        'rtf',
        'png',
        'msg',
    ]),
};

const SORT_TYPES = {
    FILE_NAME_ASC: 'File name (A -> Z)',
    FILE_NAME_DESC: 'File name (Z -> A)',
    CREATION_TIME_DESC: 'default',
} as const;

const EMPTY_STATE_CONFIG = {
    0: {
        icon: <IconAddDocs />,
        title: 'Hey, upload a file to get started.',
    },
    1: {
        icon: <IconAddPhoto />,
        title: 'Hey, upload a photo to get started.',
    },
} as const;

function Card({
    documentsList,
    selectedSortBy,
    selectedFileTypeTab,
}: {
    documentsList: DocumentsProps[];
    selectedSortBy: string;
    selectedFileTypeTab: number;
}) {
    const getSortingDataFromSortBy = (data: DocumentsProps[]) => {
        const sortedData = [...data];

        switch (selectedSortBy) {
            case SORT_TYPES.FILE_NAME_ASC:
                return sortedData.sort((a, b) =>
                    a.properties.filename.localeCompare(
                        b.properties.filename,
                        undefined,
                        {
                            sensitivity: 'base',
                        }
                    )
                );
            case SORT_TYPES.FILE_NAME_DESC:
                return sortedData.sort((a, b) =>
                    b.properties.filename.localeCompare(
                        a.properties.filename,
                        undefined,
                        {
                            sensitivity: 'base',
                        }
                    )
                );
            default:
                return sortedData.sort(
                    (a, b) =>
                        new Date(b.properties.creationtime).getTime() -
                        new Date(a.properties.creationtime).getTime()
                );
        }
    };

    const filterFiles = (file: DocumentsProps): boolean => {
        const filename = file.properties.filename.toLowerCase();
        const extension = filename.split('.').pop() || '';

        const targetExtensions =
            selectedFileTypeTab === 0
                ? FILE_EXTENSIONS.DOCUMENTS
                : FILE_EXTENSIONS.IMAGES;

        return targetExtensions.has(extension);
    };

    const processedData = useMemo(() => {
        if (!documentsList.length) return [];

        const filteredData = documentsList.filter(filterFiles);
        return getSortingDataFromSortBy(filteredData);
    }, [documentsList, selectedSortBy, selectedFileTypeTab]);

    if (!documentsList.length) {
        const config =
            EMPTY_STATE_CONFIG[
                selectedFileTypeTab as keyof typeof EMPTY_STATE_CONFIG
            ];
        return (
            <FIleEmptyState iconComponent={config.icon} title={config.title} />
        );
    }

    if (!processedData.length) {
        const config =
            EMPTY_STATE_CONFIG[
                selectedFileTypeTab as keyof typeof EMPTY_STATE_CONFIG
            ];
        return (
            <FIleEmptyState iconComponent={config.icon} title={config.title} />
        );
    }

    return (
        <>
            {processedData.map((item) => (
                <DocumentActionCard
                    key={item.name}
                    item={item}
                    isShowBorderBottom={false}
                />
            ))}
        </>
    );
}

function DocumentComponent() {
    const {
        fileListLoading,
        documentsList,
        selectedFileTypeTab,
        searchDocsFile,
        selectedSortBy,
    } = useYourFiles();

    const searchFilterDocuments = documentsList.filter((document) =>
        !searchDocsFile.length
            ? document
            : document.properties?.filename
                  ?.toLowerCase()
                  .includes(searchDocsFile.toLowerCase())
    );

    if (fileListLoading) {
        return (
            <>
                {[...Array(4)].map(() => (
                    <LoadingDocumentCard />
                ))}
            </>
        );
    }
    return (
        <Card
            documentsList={searchFilterDocuments}
            selectedSortBy={selectedSortBy}
            selectedFileTypeTab={selectedFileTypeTab}
        />
    );
}

function Documents() {
    return (
        <Row direction="column" gap="10px">
            <DocumentComponent />
        </Row>
    );
}

export default Documents;
